import { Controllers } from "@/CommonElements/Controller";
import { DatePicker, Flex } from "antd";
import React, { memo, useEffect, useMemo } from "react";
import { Button } from "react-bootstrap";
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import dayjs from "dayjs";
import { cn, dateFormat, getDayNameInArabic, handleFormErrors, PickKeyPairs } from "@/utils/helpers";
import { AntInput } from "@/CommonElements/AntInput";
import { useCachedBasicData } from "@/utils/hooks/useCachedBasicData";
import { LabelValue } from "@/Component/Value";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";
import { SelectCachedOptions } from "@/Component/SelectCachedOptions/SelectCachedOptions";
import { DATE_TIME_FORMAT_INPUT, NUSUK_CITY_ID, TIME_FORMAT } from "@/utils/constants";
import { useLocation } from "react-router";
import { AntSelect } from "@/CommonElements/AntSelect";
import { usePilgrimGroupNames } from "@/services/basicData/basicData";

export const CampaignDetailsForm = memo(({
  mutation,
  selectedSegment,
  initialData = {},
  onClose,
  isOpen,
  totalCapacity,
  extraData = [],
}) => {
  const { create, update, isPending } = mutation;
  // =======
  // Hooks
  // =======
  const location = useLocation();
  const { t } = useTranslation();
  const { getLocalizedDate, getLocalizedText } = useLocalizedText();
  const form = useForm();
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = form;
  const { cachedBasicData } = useCachedBasicData();

  // Reset when the modal opens or the segment being edited changes — not when `selectedSegment`
  // is a new object reference for the same id (e.g. after a refetch), or clearing fields will snap back.
  useEffect(() => {
    if (!isOpen) return;

    const getIds = PickKeyPairs(selectedSegment, ["contract_id", "entry_port_id", "airline_id", "direction_id", "transport_method_id"]);
    const stringIds = Object.fromEntries(
      Object.entries(getIds).map(([key, value]) => [
        key,
        value != null && value !== "" ? String(value) : "",
      ])
    );

    form.reset({
      transport_method_id: "1",
      ...selectedSegment,
      ...stringIds
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- selectedSegment intentionally omitted; sync only on open / segment id change
  }, [isOpen, selectedSegment?.id]);

  // =======
  // Controllers
  // =======

  const isGroups = location.pathname.includes("groups");

  const rawGroupNamesFromMapping = useMemo(() => {
    const names =
      selectedSegment?.pilgrim_group_name_mapping?.raw_group_names ??
      initialData?.pilgrim_group_name_mapping?.raw_group_names;
    return Array.isArray(names) ? names : [];
  }, [
    selectedSegment?.pilgrim_group_name_mapping?.raw_group_names,
    initialData?.pilgrim_group_name_mapping?.raw_group_names,
  ]);

  const shouldFetchPilgrimGroupNames =
    isGroups &&
    rawGroupNamesFromMapping.length === 0 &&
    (!!initialData?.delegation || !!initialData?.company_id);

  const { data: pilgrimGroupNamesRes, isFetching: isFetchingPilgrimGroupNames } =
    usePilgrimGroupNames({
      mission_id: initialData?.delegation,
      company_id: initialData?.company_id,
      enabled: shouldFetchPilgrimGroupNames,
    });

  const nusukGroupNameOptions = useMemo(() => {
    if (rawGroupNamesFromMapping.length > 0) {
      return rawGroupNamesFromMapping.map((name) => ({
        value: name,
        label: name,
      }));
    }
    const list = pilgrimGroupNamesRes?.data?.data ?? [];
    return (Array.isArray(list) ? list : []).map((m) => ({
      value: m.raw_group_name,
      label:
        getLocalizedText(m.raw_group_name_ar, m.raw_group_name_en) ??
        m.raw_group_name ??
        "",
    }));
  }, [rawGroupNamesFromMapping, pilgrimGroupNamesRes, getLocalizedText]);

  const nusukSelectLoading =
    shouldFetchPilgrimGroupNames && isFetchingPilgrimGroupNames;

  const controllers = [
    ...(isGroups
      ? [
          {
            labelProps: {
              children: t("NUSUK_GROUP_NAME"),
              className: "required",
            },
            controllerProps: {
              name: "group_name",
              rules: {
                required: t("FIELD_REQUIRED"),
              },
              render: ({ field }) => {
                const current = field.value;
                const hasCurrent =
                  current != null &&
                  current !== "" &&
                  !nusukGroupNameOptions.some((o) => o.value === current);
                const optionsWithCurrent =
                  hasCurrent && current
                    ? [{ value: current, label: String(current) }, ...nusukGroupNameOptions]
                    : nusukGroupNameOptions;

                return (
                  <AntSelect
                    {...field}
                    showSearch
                    optionFilterProp="label"
                    allowClear
                    id="group_name"
                    placeholder={t("NUSUK_GROUP_NAME")}
                    options={optionsWithCurrent}
                    loading={nusukSelectLoading}
                    status={errors.mapped_group_name ? "error" : undefined}
                  />
                );
              },
            },
          },
        ]
      : []),
    {
      labelProps: {
        children: t("FLIGHTS_MODAL_FLIGHT_NO_LABEL"),
      },
      controllerProps: {
        name: "flight_number",
        rules: {
          maxLength: {
            value: 4,
            message: t("MAX_LENGTH", {
              max: 4,
            }),
          },
          pattern: {
            value: /^[0-9]+$/,
            message: t("FLIGHT_NUMBER_NUMERIC_ONLY", {
              defaultValue: "Flight number must contain only numbers",
            }),
          },
        },
        render: ({ field }) => (
          <AntInput
            {...field}
            id="flight_number"
            type="text"
            placeholder={t("FLIGHTS_MODAL_FLIGHT_NO_PLACEHOLDER")}
            status={errors.flight_number ? "error" : undefined}
          />
        ),
      },
    },
    {
      labelProps: {
        children: t("ARRIVAL_DATE"),
        className: "required",
      },
      controllerProps: {
        name: "flight_date",
        rules: {
          required: t("FIELD_REQUIRED"),
        },
        render: ({ field }) => (
          <DatePicker
            size="large"
            value={field.value ? dayjs(field.value) : null}
            onChange={(date) => {
              field.onChange(date);

              const dayName = getDayNameInArabic(date);
              form.setValue("day_name_ar", dayName);
            }}
            id="flight_date"
            className="w-100"
            placeholder={t("FLIGHTS_MODAL_ARRIVAL_DATE_PLACEHOLDER")}
            status={errors.flight_date ? "error" : undefined}
            locale="ar"
            format={DATE_TIME_FORMAT_INPUT}
            showTime={{
              format: TIME_FORMAT,
              minuteStep: 1,
            }}
            disabledDate={(current) =>
              current && current < dayjs().startOf("day")
            }
          />
        ),
      },
    },

    {
      labelProps: {
        children: t("FLIGHTS_MODAL_ARRIVAL_DAY_LABEL"),
      },
      controllerProps: {
        name: "day_name_ar",
      },
      isViewMode: true,
      watch: form.watch,
    },
    {
      labelProps: {
        children: t("FLIGHTS_MODAL_AIRLINE_LABEL"),
      },
      controllerProps: {
        name: "airline_id",
        render: ({ field }) => (
          <SelectCachedOptions
            {...field}
            id="airline_id"
            showSearch
            placeholder={t("FLIGHTS_MODAL_AIRLINE_PLACEHOLDER")}
            status={errors.airline_id ? "error" : undefined}
            optionKey="airlines"
            options={(airline) => ({
              value: String(airline.id),
              label: [getLocalizedText(airline.name_ar, airline.name_en), `(${airline.code})`].join(" "),
            })}
          />
        ),
      },
    },
    {
      labelProps: {
        children: t("UPCOMING_COL_PORT"),
        className: "required",
      },
      controllerProps: {
        name: "entry_port_id",
        rules: {
          required: t("FIELD_REQUIRED"),
        },
        render: ({ field }) => (
          <SelectCachedOptions
            {...field}
            id="entry_port_id"
            showSearch
            placeholder={t("FLIGHTS_MODAL_ENTRY_PORT_PLACEHOLDER")}
            status={errors.entry_port_id ? "error" : undefined}
            optionKey="ports"
            options={(port) => ({
              value: String(port.id),
              label: (
                <LabelValue.Value
                  value={
                    getLocalizedText(port?.name_ar, port?.name_en) || port?.name
                  }
                />
              ),
            })}
          />
        ),
      },
    },

    {
      labelProps: {
        children: t("FLIGHTS_MODAL_COMING_FROM_LABEL"),
        className: "required",
      },
      controllerProps: {
        name: "coming_from_id",
        rules: {
          required: t("FIELD_REQUIRED"),
        },
        render: ({ field }) => (
          <SelectCachedOptions
            {...field}
            id="coming_from_id"
            showSearch
            placeholder={t("FLIGHTS_MODAL_COMING_FROM_PLACEHOLDER")}
            status={errors.coming_from_id ? "error" : undefined}
            optionKey="directions"
            options={(direction) => ({
              value: direction.id,
              label: <LabelValue.Value value={direction.name} />,
            })}
          />
        ),
      },
    },

    {
      labelProps: {
        children: t("FLIGHTS_MODAL_DIRECTION_LABEL", {
          defaultValue: "الاتجاه",
        }),
        className: "required",
      },
      controllerProps: {
        name: "direction_id",
        rules: {
          required: t("FIELD_REQUIRED"),
        },
        render: ({ field }) => (
          <SelectCachedOptions
            {...field}
            onChange={(value) => {
              field.onChange(value);
              form.setValue("contract_id", '');
            }}
            showSearch
            id="direction_id"
            filter={(d) => d.id === 1 || d.id === 2}
            status={errors.direction_id ? "error" : undefined}
            placeholder={t("FLIGHTS_MODAL_DIRECTION_PLACEHOLDER", {
              defaultValue: "اختر الاتجاه",
            })}
            optionKey="directions"
            options={(direction) => ({
              value: String(direction.id),
              label: <LabelValue.Value value={direction.name} />,
            })}
          />
        ),
      },
    },

    {
      labelProps: {
        children: t("FLIGHTS_MODAL_HOUSING_CONTRACT_LABEL"),
      },
      controllerProps: {
        name: "contract_id",
        render: ({ field }) => {
          const direction = form.watch("direction_id");
          const nusukCityId =
            String(direction) === "1"
              ? NUSUK_CITY_ID.makkah
              : NUSUK_CITY_ID.madina;

          return (
            <SelectCachedOptions
              {...field}
              id="contract_id"
              showSearch
              maxTagCount={2}
              maxTagPlaceholder={(omittedValues) => `+${omittedValues.length}`}
              placeholder={t("FLIGHTS_MODAL_HOUSING_CONTRACT_PLACEHOLDER")}
              status={errors.contract_id ? "error" : undefined}
              filter={(contract) => String(contract.city_id) === String(nusukCityId)}
              optionKey="contracts"
              options={(contract) => ({
                value: String(contract.id),
                label: (
                  <LabelValue.Value
                    value={`${contract.id} - ${contract.house_commercial_name_ar}`}
                  />
                ),
              })}
            />
          );
        },
      },
    },
    {
      labelProps: {
        children: t("CONTRACT_START_DATE"),
      },
      controllerProps: {
        name: "contract_start_date",
      },
      isViewMode: true,
      renderValue: () => {
        const contract_id = form.watch("contract_id");
        const contract = cachedBasicData.contracts.find(
          (contract) => String(contract.id) === String(contract_id)
        );
        if (!contract) return "-";
        return contract.contract_start_date
          ? getLocalizedDate(contract.contract_start_date)
          : "-";
      },
    },
    {
      labelProps: {
        children: t("CONTRACT_END_DATE"),
      },
      controllerProps: {
        name: "contract_end_date",
      },
      isViewMode: true,
      renderValue: () => {
        const contract_id = form.watch("contract_id");
        const contract = cachedBasicData.contracts.find(
          (contract) => String(contract.id) === String(contract_id)
        );
        if (!contract) return "-";
        return contract.contract_end_date
          ? getLocalizedDate(contract.contract_end_date)
          : "-";
      },
    },

    {
      labelProps: {
        children: t("FLIGHTS_MODAL_NUMBER_OF_BUSES_LABEL"),
      },
      controllerProps: {
        name: "buses_count",
        rules: {
          validate: (value) => {
            if (!value) return true; // Allow empty
            if (!/^\d+$/.test(value)) {
              return t("MUST_BE_NUMBERS_DIGITS", {
                defaultValue: "Must be numbers/digits",
              });
            }
            if (value.length > 2) {
              return t("CAMPAIGNS_FORM_MAX_TWO_DIGITS", {
                defaultValue: t("MAX_TWO_DIGITS", {
                  defaultValue: "Max 2 digits",
                }),
              });
            }
            return true;
          },
        },
        render: ({ field }) => (
          <AntInput
            {...field}
            id="buses_count"
            type="text"
            placeholder={t("FLIGHTS_MODAL_NUMBER_OF_BUSES_PLACEHOLDER", {
              defaultValue: "أدخل عدد الحافلات",
            })}
            status={errors.buses_count ? "error" : undefined}
            maxLength={2}
          />
        ),
      },
    },
    {
      labelProps: {
        children: t("FLIGHTS_MODAL_TRANSPORT_METHOD_LABEL"),
        className: "required",
      },
      controllerProps: {
        name: "transport_method_id",
        rules: {
          required: t("FIELD_REQUIRED"),
        },
        render: ({ field }) => (
          <SelectCachedOptions
            {...field}
            id="transport_method_id"
            showSearch
            placeholder={t("CHOOSE_TRANSPORT_METHOD")}
            status={errors.transport_method_id ? "error" : undefined}
            optionKey="transportation"
            options={(transportMethod) => ({
              value: String(transportMethod.id),
              label: <LabelValue.Value value={transportMethod.name} />,
            })}
          />
        ),
      },
    },

    {
      labelProps: {
        children: t("FLIGHTS_MODAL_PILGRIMS_COUNT_LABEL"),
        className: "required",
      },
      controllerProps: {
        name: "capacity",
        rules: {
          required: t("FIELD_REQUIRED"),
          pattern: {
            value: /^[0-9]{1,3}$/,
            message: t("CAMPAIGNS_FORM_MAX_THREE_DIGITS", {
              defaultValue: t("CAMPAIGNS_FORM_NUMBER_REQUIRED"),
            }),
          },
          min: {
            value: 1,
            message: t("CAMPAIGNS_FORM_MIN_ONE"),
          },
          ...(!isGroups
            ? {
                max: {
                  value: 250,
                  message: t("CAMPAIGNS_FORM_MAX_250"),
                },
              }
            : {}),
          validate: (value) => {
            // Groups: no 250 cap or campaign-style remaining capacity; up to 999 (3 digits).
            if (isGroups) {
              return true;
            }

            const defaultCapacity = selectedSegment?.capacity ?? 0;
            const remainingCapacity =
              250 - Number(totalCapacity ?? 0) + Number(defaultCapacity ?? 0);
            const capacity = form.getFieldState("capacity");

            if (!capacity.isTouched || Number(value) === Number(defaultCapacity))
              return true;

            if (remainingCapacity <= 0) {
              return t("CAMPAIGNS_FORM_NO_REMAINING_CAPACITY");
            }

            // 7. Exceeds remaining capacity
            if (value > remainingCapacity) {
              return t("CAMPAIGNS_FORM_EXCEEDS_REMAINING_CAPACITY", {
                remaining: remainingCapacity,
              });
            }

            // 8. Exceeds maximum allowed (250)
            if (value > 250) {
              return t("CAMPAIGNS_FORM_EXCEEDS_MAXIMUM_CAPACITY");
            }

            // 9. totalCapacity is invalid/undefined
            if (
              totalCapacity === null ||
              totalCapacity === undefined ||
              isNaN(Number(totalCapacity))
            ) {
              return t("CAMPAIGNS_FORM_TOTAL_CAPACITY_ERROR");
            }

            return true;
          },
        },
        render: ({ field }) => (
          <AntInput
            {...field}
            id="capacity"
            type="text"
            placeholder={t("DETAILS_CAPACITY_PLACEHOLDER")}
            status={errors.capacity ? "error" : undefined}
            maxLength={3}
          />
        ),
      },
    },
    ...(!isGroups ? [{
      labelProps: {
        children: t("CAMPAIGNS_FORM_WORKERS_COUNT_LABEL"),
      },
      controllerProps: {
        name: "employee_number",
        rules: {
          pattern: {
            value: /^[0-9]{1,3}$/,
            message: t("CAMPAIGNS_FORM_MAX_THREE_DIGITS", {
              defaultValue: t("CAMPAIGNS_FORM_NUMBER_REQUIRED"),
            }),
          },
          min: {
            value: 1,
            message: t("CAMPAIGNS_FORM_MIN_ONE"),
          },
        },
        render: ({ field }) => (
          <AntInput
            {...field}
            id="employee_number"
            type="text"
            placeholder={t("DETAILS_WORKERS_COUNT_PLACEHOLDER")}
            status={errors.employee_number ? "error" : undefined}
            maxLength={3}
          />
        ),
      },
    }] : []),
    ...extraData,
  ];

  const formattedDates = (data) => {
    return {
      flight_date: dateFormat(data.flight_date),
      real_datetime: dateFormat(data.real_datetime),
    };
  };
  // =======
  // Handlers
  // =======
  const resetFormToDefaults = () => {
    form.reset({
      transport_method_id: "1",
    });
  };

  const onSave = (data) => {
    create(
      { ...data, ...formattedDates(data) },
      {
        onSuccess: () => {
          resetFormToDefaults();
          onClose(null);
        },
        onError: (error) => {
          handleFormErrors(error, form.setError, t, "CREATE_PILGRIM_GROUP_NAME_MAPPINGS_FAILED");
        },
      }
    );
  };

  const onUpdate = (data) => {
    update(
      {
        segmentId: selectedSegment.id,
        ...data,
        ...formattedDates(data),
      },
      {
        onSuccess: () => {
          resetFormToDefaults();
          onClose(null);
        },
        onError: (error) => {
          handleFormErrors(error, form.setError, t, "UPDATE_PILGRIM_GROUP_NAME_MAPPINGS_FAILED");
        },
      }
    );
  };

  // =======
  // Render
  // =======

  const title = location.pathname.includes("groups") ? t("GROUP") : t("CAMPAIGN");
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      centered
      backdrop="static"
      keyboard={false}
    >
      <form onSubmit={selectedSegment?.id ? handleSubmit(onUpdate) : handleSubmit(onSave)}>
        <ModalHeader className="sticky-top">
          <span className="fw-bold H5">
            {selectedSegment
              ? t("FLIGHTS_MODAL_TITLE_EDIT", { campaign_name: title })
              : t("FLIGHTS_MODAL_TITLE_ADD", { campaign_name: title })}
          </span>
        </ModalHeader>

        <ModalBody>
          <Controllers
            controllers={controllers?.map((controller) => ({
              ...controller,
              labelProps: {
                ...controller.labelProps,
                className: cn(controller.labelProps?.className, "fw-bold"),
              },
              controllerProps: {
                ...controller.controllerProps,
                control,
                errors,
              },
            }))}
          />
        </ModalBody>

        <ModalFooter className="sticky-bottom bg-white">
          <Flex justify="start" gap={2} className="w-100">
            <Button type="submit" className="btn btn-primary btn-xs">
              {isPending ? <Spinner animation="border" size="sm" /> : null}
              {t("SAVE")}
            </Button>
            <Button
              type="button"
              className="btn btn-danger btn-xs"
              onClick={() => onClose(null)}
            >
              {t("CANCEL")}
            </Button>
          </Flex>
        </ModalFooter>
      </form>
    </Modal>
  );
});
